import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";

const CONSENT_KEY = "aurelo_cookie_consent";

// Bottom-left cookie consent banner. Remembers the choice in localStorage.
export const CookieConsent = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    try { if (!localStorage.getItem(CONSENT_KEY)) setShow(true); } catch {}
  }, []);
  const decide = (choice) => {
    try { localStorage.setItem(CONSENT_KEY, choice); } catch {}
    setShow(false);
  };
  if (!show) return null;
  return (
    <div data-testid="cookie-consent" className="fixed z-[200] bottom-4 left-4 right-4 sm:right-auto sm:max-w-md">
      <div className="rounded-2xl bg-[#0B102B] text-white border border-white/10 shadow-[0_20px_60px_rgba(11,16,43,0.45)] p-5">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Cookie className="h-4 w-4 text-[#A855F7]" /></div>
          <div className="min-w-0">
            <div className="text-sm font-semibold">We value your privacy</div>
            <p className="text-xs text-slate-300 mt-1 leading-relaxed">
              We use cookies to improve your experience, analyze traffic, and personalize content. Read our{" "}
              <Link to="/legal/cookies" data-testid="cookie-consent-link" className="text-[#A855F7] hover:underline font-medium">Cookie Notice</Link>.
            </p>
          </div>
          <button onClick={() => decide("dismissed")} aria-label="Close" className="ml-auto text-slate-400 hover:text-white shrink-0"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <Button data-testid="cookie-accept" onClick={() => decide("accepted")} className="aurelo-gradient text-white h-9 flex-1">Accept all</Button>
          <Button data-testid="cookie-reject" onClick={() => decide("rejected")} variant="outline" className="h-9 flex-1 bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white">Reject non-essential</Button>
        </div>
      </div>
    </div>
  );
};

// Floating "back to top" button (bottom-center) — fades in after scrolling down.
export const BackToTop = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button
      data-testid="back-to-top"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      title="Back to top"
      className={`fixed z-[150] left-1/2 -translate-x-1/2 bottom-6 h-11 w-11 rounded-full aurelo-gradient text-white shadow-[0_10px_30px_rgba(124,58,237,0.40)] flex items-center justify-center hover:shadow-[0_14px_36px_rgba(124,58,237,0.5)] transition-all duration-200 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
    >
      <ArrowUp className="h-5 w-5" />
    </button>
  );
};
